import React from 'react';
import { Card, Tag, Alert, Tooltip, Progress, Row, Col } from 'antd';
import {
  INK, NEUTRAL, UNDER, fmtMoney, fmtPct,
} from './shared';

/**
 * Where this month's collections stand against where they should be by now.
 *
 * Lives on the cashflow view, not on GP Control. It is a cash number, and a tab
 * carrying one cash figure among accrual ones makes a reader check the basis of
 * every number before they can use any of them.
 *
 * Pace, not total: a third of target on the 10th is fine and a third on the
 * 28th is not, and a bare percentage of budget cannot tell the two apart.
 */

const LEVELS = {
  warning: { colour: '#d48806', label: 'Behind pace', tone: 'warning' },
  serious: { colour: '#d4380d', label: 'Well behind pace', tone: 'warning' },
  critical: { colour: '#a8071a', label: 'Critically behind pace', tone: 'error' },
};

export default function ReceiptPace({ receipts, collectionRatio }) {
  if (!receipts) return null;
  const {
    target, received, expectedByNow, behindPct, level, daysElapsed, daysInMonth, basis,
  } = receipts;

  if (!target) {
    return (
      <Alert
        type="info"
        showIcon
        message={(
          <span style={{ fontSize: 12 }}>
            Receipts cannot be tracked this month: nothing has been invoiced yet and no revenue
            forecast has been saved, so there is no target to measure collections against.
          </span>
        )}
      />
    );
  }

  const pct = target ? Math.min(100, Math.round((received / target) * 100)) : 0;
  const pacePct = target ? Math.round((expectedByNow / target) * 100) : 0;
  const lv = level ? LEVELS[level] : null;

  return (
    <Card
      size="small"
      styles={{ body: { padding: '12px 14px' } }}
      style={lv ? { borderColor: lv.colour } : undefined}
    >
      <Row gutter={[16, 12]} align="middle">
        <Col xs={24} md={11}>
          <div style={{ fontSize: 12, color: NEUTRAL, marginBottom: 4 }}>
            Cash collected this month · day {daysElapsed} of {daysInMonth}
          </div>
          <Progress
            percent={pct}
            success={{ percent: Math.min(pct, pacePct), strokeColor: UNDER }}
            strokeColor={lv ? lv.colour : UNDER}
            format={() => `${pct}%`}
          />
          <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 2 }}>
            pace to today is {pacePct}% of target
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div style={{ fontSize: 11, color: NEUTRAL }}>Received</div>
          <div style={{ fontSize: 18, color: INK, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(received)}</div>
        </Col>
        <Col xs={12} md={4}>
          <div style={{ fontSize: 11, color: NEUTRAL }}>Expected by now</div>
          <div style={{ fontSize: 18, color: NEUTRAL, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(expectedByNow)}</div>
        </Col>
        <Col xs={24} md={5}>
          {lv ? (
            <Tooltip title="Spending control should be tightened until collections catch up.">
              <Tag color={lv.tone === 'error' ? 'red' : 'orange'} style={{ fontSize: 12, padding: '4px 8px' }}>
                {lv.label} — {fmtPct(behindPct, 0)} behind
              </Tag>
            </Tooltip>
          ) : (
            <Tag color="green" style={{ fontSize: 12, padding: '4px 8px' }}>On pace</Tag>
          )}
        </Col>
      </Row>
      <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 8 }}>
        Target {fmtMoney(target)} — {basis === 'forecast' ? 'this month\u2019s forecast revenue' : 'what has been invoiced so far'}
        {collectionRatio != null
          ? `, scaled by the ${Math.round(collectionRatio * 100)}% of invoiced revenue actually collected over the completed months in view`
          : ', with no collection history to scale by'}.
      </div>
    </Card>
  );
}
